import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader, Lock, Shield, Key } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';

/**
 * LoginPage
 * --------------------------------------------------------------------
 * Two-step login flow:
 *   1. Username + password (trusted-device fast-path returns token immediately).
 *   2. 6-digit OTP + "Trust this device" checkbox.
 */
export default function LoginPage() {
  const { login, verifyOtp, status } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo =
    (location.state && location.state.from && location.state.from.pathname) ||
    '/website_admin';

  const [step, setStep] = useState(1);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [trustDevice, setTrustDevice] = useState(true);
  const [adminId, setAdminId] = useState(null);
  const [otpDevDisplay, setOtpDevDisplay] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (status === 'authenticated') {
      navigate(redirectTo, { replace: true });
    }
  }, [status, navigate, redirectTo]);

  const handleStep1 = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await login({ username: username.trim(), password });
      if (res.authenticated) {
        navigate(redirectTo, { replace: true });
        return;
      }
      if (res.requiresOtp) {
        setAdminId(res.adminId);
        setOtpDevDisplay(res.otpDevDisplay || '');
        if (res.otpDevDisplay) setOtp(res.otpDevDisplay);
        setStep(2);
      } else {
        setError('Login did not return a token. Please try again.');
      }
    } catch (err) {
      setError(err && err.message ? err.message : 'Login failed.');
    } finally {
      setBusy(false);
    }
  };

  const handleStep2 = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await verifyOtp({
        adminId,
        otp: otp.trim(),
        trustDevice,
      });
      if (res.authenticated) {
        navigate(redirectTo, { replace: true });
      } else {
        setError('OTP could not be verified.');
      }
    } catch (err) {
      setError(err && err.message ? err.message : 'OTP verification failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-indigo-50 to-white px-4 py-12">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-indigo-700 to-indigo-900 px-6 py-5 text-white">
            <div className="flex items-center gap-2">
              <Shield size={20} />
              <h1 className="text-lg font-semibold tracking-tight">Website Admin</h1>
            </div>
            <p className="text-indigo-100 text-xs mt-1">
              Trivandrum Scottish School &middot; Inline CMS
            </p>
          </div>

          <div className="px-6 py-6">
            {step === 1 ? (
              <form onSubmit={handleStep1} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Username
                  </label>
                  <input
                    type="text"
                    autoComplete="username"
                    autoFocus
                    required
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="w-full px-3 py-2 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Password
                  </label>
                  <div className="relative">
                    <Lock
                      size={14}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                    />
                    <input
                      type="password"
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none text-sm"
                    />
                  </div>
                </div>

                {error && (
                  <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={busy}
                  className="w-full inline-flex items-center justify-center gap-2 bg-indigo-700 hover:bg-indigo-800 disabled:opacity-60 text-white font-medium py-2.5 rounded-md text-sm transition-colors"
                >
                  {busy && <Loader size={14} className="animate-spin" />}
                  {busy ? 'Signing in…' : 'Continue'}
                </button>
              </form>
            ) : (
              <form onSubmit={handleStep2} className="space-y-4">
                <div className="text-xs text-gray-500 flex items-center gap-1.5">
                  <Key size={12} />
                  We sent a 6-digit code to your registered channel.
                </div>

                {otpDevDisplay && (
                  <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs">
                    <div className="font-semibold text-amber-800 uppercase tracking-wider text-[10px]">
                      Dev OTP
                    </div>
                    <div className="font-mono text-amber-900 text-base tracking-widest mt-0.5">
                      {otpDevDisplay}
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    One-time code
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    required
                    autoFocus
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ''))}
                    className="w-full px-3 py-2.5 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none text-center font-mono text-lg tracking-[0.5em]"
                  />
                </div>

                <label className="flex items-center gap-2 text-xs text-gray-700">
                  <input
                    type="checkbox"
                    checked={trustDevice}
                    onChange={(e) => setTrustDevice(e.target.checked)}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  Trust this device for 30 days
                </label>

                {error && (
                  <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
                    {error}
                  </div>
                )}

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setStep(1);
                      setOtp('');
                      setError(null);
                    }}
                    className="flex-1 py-2.5 rounded-md text-sm border border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={busy || otp.length !== 6}
                    className="flex-[2] inline-flex items-center justify-center gap-2 bg-indigo-700 hover:bg-indigo-800 disabled:opacity-60 text-white font-medium py-2.5 rounded-md text-sm transition-colors"
                  >
                    {busy && <Loader size={14} className="animate-spin" />}
                    {busy ? 'Verifying…' : 'Verify & Sign in'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>

        <p className="text-center text-[11px] text-gray-400 mt-4">
          Access is restricted to authorized website editors.
        </p>
      </div>
    </div>
  );
}
